/**
 * @spec docs/specs/features/staking-position-ui.md#design-system
 *
 * Mobile navigation menu with slide-out drawer.
 * Keeps wallet status visible in header, moves nav to drawer.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { useAuth } from '@/hooks/useAuth';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { showToast, TOAST_MESSAGES } from '@/lib/toast';
import { Icon } from '@/components/ui/icons';
import { PLAY_URL, type NavItem } from './nav';
import { NavBadge } from './NavBadge';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  isConnected: boolean;
  isAuthenticated: boolean;
  navItems: NavItem[];
  email?: string | null;
}

const ROW =
  'flex h-12 w-full items-center gap-3 rounded-control px-3 text-[15px] font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300';

export function MobileMenu({
  isOpen,
  onClose,
  isConnected,
  isAuthenticated,
  navItems,
  email,
}: MobileMenuProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const { logout, login } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Set when the menu closes to hand off to the Privy sign-in dialog: focus must stay with that
  // dialog rather than return to the menu button behind it. Cleared on the next open.
  const [loginHandoff, setLoginHandoff] = useState(false);
  useEffect(() => {
    if (isOpen) setLoginHandoff(false);
  }, [isOpen]);

  // Focus moves into the drawer on open, Tab cannot wander to the page behind, and focus
  // returns to the menu button on close (unless the close opened sign-in).
  useFocusTrap(menuRef, isOpen, { restoreFocus: !loginHandoff });

  // Close first, so the sign-in dialog opens over the page rather than over the open drawer.
  const handleLogin = useCallback(() => {
    setLoginHandoff(true);
    onClose();
    login();
  }, [login, onClose]);

  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      showToast.success(
        isAuthenticated ? TOAST_MESSAGES.LOGGED_OUT : TOAST_MESSAGES.WALLET_DISCONNECTED
      );
      onClose();
      navigate('/');
    } catch {
      showToast.error(TOAST_MESSAGES.LOGOUT_FAILED);
    } finally {
      setIsLoggingOut(false);
    }
  }, [logout, onClose, navigate, isAuthenticated]);

  // Close menu on route change
  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      // Delay to prevent immediate close from toggle button
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/60 backdrop-blur-sm',
          'transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={menuRef}
        className={cn(
          'fixed right-0 top-0 z-50 flex h-full w-[300px] max-w-[85vw] flex-col',
          'border-l border-white/[0.08] bg-surface-1 shadow-2xl',
          'transition-[transform,visibility] duration-300 ease-out',
          isOpen ? 'visible translate-x-0' : 'invisible translate-x-full'
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Header */}
        <div className="flex h-16 items-center justify-between border-b border-white/[0.06] px-4">
          <span className="eyebrow">Menu</span>
          <button
            type="button"
            onClick={onClose}
            className="-mr-1 inline-flex size-10 items-center justify-center rounded-control text-text-secondary transition-colors hover:bg-white/[0.06] hover:text-text-primary"
            aria-label="Close menu"
          >
            <Icon name="x" size={18} />
          </button>
        </div>

        {/* User info */}
        {email && (
          <div className="border-b border-white/[0.06] px-5 py-3">
            <span className="text-xs text-text-muted">Signed in as</span>
            <p className="truncate text-sm text-text-primary">{email}</p>
          </div>
        )}

        {/* Navigation */}
        <nav aria-label="Primary" className="flex-1 space-y-1 overflow-y-auto p-3">
          {navItems.map((item) => {
            const active = item.isActive(location.pathname);
            return (
              <Link
                key={item.key}
                to={item.to}
                onClick={onClose}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  ROW,
                  active
                    ? 'bg-white/[0.08] text-text-primary'
                    : 'text-text-secondary hover:bg-white/[0.04] hover:text-text-primary'
                )}
              >
                <span
                  className={cn(
                    'inline-flex size-8 items-center justify-center rounded-[10px]',
                    active ? 'bg-brand-500/20 text-brand-200' : 'bg-white/[0.04]'
                  )}
                >
                  <Icon name={item.icon} size={16} />
                </span>
                {item.label}
                <NavBadge label={item.badge} className="ml-auto" />
              </Link>
            );
          })}

          <a
            href={PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className={cn(ROW, 'text-text-secondary hover:bg-white/[0.04] hover:text-text-primary')}
          >
            <span className="inline-flex size-8 items-center justify-center rounded-[10px] bg-white/[0.04]">
              <Icon name="play" size={14} />
            </span>
            Play poker
            <span className="sr-only">(opens in a new tab)</span>
            <Icon name="arrow-up-right" size={14} className="ml-auto opacity-60" />
          </a>
        </nav>

        {/* Sign in for visitors: the header's Sign In is hidden below the sm breakpoint */}
        {!isAuthenticated && !isConnected && (
          <div className="border-t border-white/[0.06] p-3 pb-6">
            <button
              type="button"
              onClick={handleLogin}
              className={cn(ROW, 'text-brand-200 hover:bg-brand-500/10')}
            >
              <span className="inline-flex size-8 items-center justify-center rounded-[10px] bg-brand-500/15">
                <Icon name="wallet" size={16} />
              </span>
              Sign in with email
            </button>
          </div>
        )}

        {/* Log Out or Sign In */}
        {(isAuthenticated || isConnected) && (
          <div className="border-t border-white/[0.06] p-3 pb-6">
            {!isAuthenticated && isConnected ? (
              <button
                type="button"
                onClick={handleLogin}
                className={cn(ROW, 'text-brand-200 hover:bg-brand-500/10')}
              >
                Sign In
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void handleLogout()}
                disabled={isLoggingOut}
                className={cn(
                  ROW,
                  'text-status-error hover:bg-status-error/10',
                  isLoggingOut && 'cursor-not-allowed opacity-50'
                )}
              >
                {isLoggingOut ? 'Logging out...' : 'Log Out'}
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
